import { ExternalLink } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { Button } from '@/common/components/ui/button';
import { TableCell, TableRow } from '@/common/components/ui/table';
import { formatDate } from '@/common/utils/DateFormatters';
import { PolicySummary } from '@/policy/api/FetchPolicyList';
import { getEpicPolicyURL } from '@/policy/utils/epicUrl';

import { PolicyStatusBadge } from './PolicyStatusBadge';

interface PolicyRowProps {
  policy: PolicySummary;
}

export const PolicyListRow = ({ policy }: PolicyRowProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const epicUrl = policy.epicPolicyUuid
    ? getEpicPolicyURL(policy.epicPolicyUuid)
    : undefined;

  const isDisabled = policy.status === 'IN_PROGRESS' || policy.isLocked;

  const handleViewClick = () => {
    if (!isDisabled) {
      navigate(`/policy/${policy.uuid}`);
    }
  };

  return (
    <TableRow
      className={`${isDisabled && 'opacity-60'}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <TableCell>
        {policy.createdAt ? formatDate(policy.createdAt, i18n.language) : null}
      </TableCell>
      <TableCell>
        <div className="truncate" title={policy.accountName}>
          {policy.accountName}
        </div>
      </TableCell>
      <TableCell>
        <div className="truncate" title={policy.fileName}>
          {policy.fileName}
        </div>
      </TableCell>
      <TableCell>
        {epicUrl ? (
          <a
            href={epicUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 hover:underline text-primary"
            onClick={(e) => e.stopPropagation()}
          >
            {policy.policyNumber}
            <ExternalLink size={14} />
          </a>
        ) : (
          policy.policyNumber
        )}
      </TableCell>
      <TableCell>{policy.insurer}</TableCell>
      <TableCell>
        {policy.expiryDate
          ? formatDate(policy.expiryDate, i18n.language)
          : null}
      </TableCell>
      <TableCell>{policy.technicalSupport}</TableCell>
      <TableCell>
        <PolicyStatusBadge status={policy.status} />
      </TableCell>
      <TableCell>{policy.assignedUserName || '-'}</TableCell>
      <TableCell>
        {policy.validatedAt
          ? formatDate(policy.validatedAt, i18n.language)
          : null}
      </TableCell>
      <TableCell>
        <Button
          onClick={handleViewClick}
          disabled={isDisabled}
          className={`transition-opacity ${
            isDisabled ? 'invisible' : isHovered ? 'opacity-100' : 'opacity-0'
          }`}
        >
          {policy.status === 'NOT_COMPLETED'
            ? t('actions.continue')
            : t('actions.start')}
        </Button>
      </TableCell>
    </TableRow>
  );
};
